// renew_v1: หน้าต่ออายุ/upgrade แพ็กเกจ — 3 step flow
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, CreditCard, MessageCircle, Copy, Upload, Loader, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../api';

// Configurable bank accounts (admin update ผ่าน DB ทีหลังก็ได้)
// renew_bank_v1: bank accounts fetched from /api/me/bank-accounts (admin-managed)
const ADMIN_TG = 'mawin1991';

const planColors = {
  Trial: { bg: '#1e293b', border: '#475569', badge: '#64748b' },
  Starter: { bg: '#0c2a1a', border: '#22c55e', badge: '#22c55e' },
  Basic: { bg: '#0c2a1a', border: '#22c55e', badge: '#22c55e' },
  Pro: { bg: '#0c1f3a', border: '#0ea5e9', badge: '#0ea5e9' },
  Premium: { bg: '#3a0c2f', border: '#ec4899', badge: '#ec4899' },
  Enterprise: { bg: '#1e0a3c', border: '#a855f7', badge: '#a855f7' },
  Unlimited: { bg: '#3c1e0a', border: '#f97316', badge: '#f97316' },
};

/* renew_lock_plan_v2: features เก็บเป็น JSON string บ้าง object บ้าง แล้วแต่เรคคอร์ด */
const parseFeatures = (pkg) => {
  try {
    return typeof pkg.features === 'string' ? JSON.parse(pkg.features) : (pkg.features || {});
  } catch (e) {
    return {};
  }
};

/* renew_lock_plan_v2: แพ็กเกจในระบบมี 2 ตระกูลปนกันในรายการเดียว
   ตระกูลแชท (STARTER/BASIC/PRO/...) กับตระกูลบิล ("ระบบบัญชีและการเงิน")
   แยกด้วย feature `bill` ไม่ใช่ด้วยชื่อ เพราะชื่อเป็นภาษาไทยและเปลี่ยนได้ */
const isBillPackage = (pkg) => !!parseFeatures(pkg).bill;

const featureLabel = (k) => ({
  bot_training: '🤖 Bot Training',
  kpi_dashboard: '📊 KPI Dashboard',
  quick_replies: '⚡ Quick Replies',
  multi_channel: '📡 Multi Channel',
  telegram_notify: '🔔 Telegram Notify'
}[k] || k);

/* renew_lock_plan_v1: การ์ดแพ็กเกจ — แยกออกมาเพื่อใช้ได้ทั้งแบบเลือกเองและแบบล็อก */
function PackageCard({ pkg, isSelected, locked }) {
  const colors = planColors[pkg.name] || planColors.Basic;
  const features = parseFeatures(pkg);
  return (
    <div style={{
      background: colors.bg, borderRadius: 14, padding: 20,
      border: '2px solid ' + ((isSelected || locked) ? colors.badge : colors.border),
      cursor: locked ? 'default' : 'pointer', transition: 'all 0.2s',
      boxShadow: (isSelected || locked) ? '0 0 20px ' + colors.badge + '50' : 'none',
    }}>
      <div style={{ display: 'inline-block', background: colors.badge, color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, marginBottom: 12 }}>
        {pkg.name.toUpperCase()}
      </div>
      <div style={{ color: '#f1f5f9', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
        {Number(pkg.price).toLocaleString()} <span style={{ fontSize: 14, color: '#94a3b8' }}>฿</span>
      </div>
      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 14 }}>/ {pkg.duration_days} วัน</div>
      <div style={{ borderTop: '1px solid #334155', paddingTop: 12, fontSize: 12, color: '#cbd5e1', lineHeight: 1.8 }}>
        <div>👥 Agents: <strong>{pkg.max_agents === 9999 ? '∞' : pkg.max_agents}</strong></div>
        <div>📡 Channels: <strong>{pkg.max_channels === 9999 ? '∞' : pkg.max_channels}</strong></div>
        <div>📚 KB: <strong>{pkg.max_kb === 9999 ? '∞' : pkg.max_kb}</strong></div>
        <div>🧾 Slip Quota: <strong>{pkg.slip_quota_monthly}/เดือน</strong></div>
        {Object.entries(features).filter(([_, v]) => v).map(([k]) => (
          <div key={k} style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{featureLabel(k)}</div>
        ))}
      </div>
      {(locked || isSelected) && (
        <div style={{ marginTop: 12, padding: '8px 12px', background: '#22c55e22', borderRadius: 8, color: '#22c55e', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
          {locked ? '★ แพ็กเกจปัจจุบันของคุณ' : '✓ เลือกแล้ว'}
        </div>
      )}
    </div>
  );
}

export default function Renew() {
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [me, setMe] = useState(null);
  /* renew_lock_plan_v2: แพ็กเกจปัจจุบันอยู่ใน license ไม่ใช่ tenant
     ของเดิมอ่าน tenant.plan ซึ่งไม่มีอยู่จริง เลยได้ค่าว่างตลอดและไม่เคยล็อกได้เลย */
  const [license, setLicense] = useState(null);
  /* renew_lock_plan_v1: รอให้รู้ทั้งแพ็กเกจและข้อมูลร้านก่อนค่อยวาด
     ไม่งั้นจะเห็นแพ็กเกจทั้งหมดแวบนึงแล้วค่อยหุบเหลืออันเดียว */
  const [meLoaded, setMeLoaded] = useState(false);
  const [pkgLoaded, setPkgLoaded] = useState(false);
  // slip_upload_v1: state สำหรับ upload สลิป
  const [slipFile, setSlipFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const navigate = useNavigate();

  const handleSubmitSlip = async () => {
    if (!slipFile || !selected) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('slip', slipFile);
      formData.append('package_id', selected.id);
      const { data } = await API.post('/api/payment/request', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRequestId(data.request_id);
      setSubmitted(true);
      toast.success('ส่งคำขอสำเร็จ!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'ส่งคำขอไม่สำเร็จ');
    }
    setSubmitting(false);
  };

  useEffect(() => {
    API.get('/api/packages').then(({ data }) => {
      const active = (data || []).filter(p => p.is_active !== false);
      setPackages(active.sort((a,b) => Number(a.price) - Number(b.price)));
    }).catch(() => toast.error('โหลดแพ็กเกจไม่สำเร็จ')).finally(() => setPkgLoaded(true));
    API.get('/api/tenants/me').then(({ data }) => {
      setMe(data.tenant || data);
      setLicense(data.license || null); /* renew_lock_plan_v2 */
    }).catch(() => {}).finally(() => setMeLoaded(true));
    API.get('/api/me/bank-accounts')
      .then(({ data }) => setBankAccounts(data.bank_accounts || []))
      .catch(() => setBankAccounts([]))
      .finally(() => setBanksLoading(false));
  }, []);

  /* renew_lock_plan_v2: ลูกค้าที่มีแพ็กเกจอยู่แล้ว ต่อได้เฉพาะของที่ตัวเองใช้อยู่
     ยกเว้นคนที่ยังใช้ Trial — ต้องเลือกได้ เพราะยังไม่เคยมีแพ็กเกจจริง

     แพ็กเกจปัจจุบันอ่านจาก license.plan (ไม่ใช่ tenant.plan — ตรงนั้นไม่มีค่า)
     เทียบชื่อแบบไม่สนตัวพิมพ์ เพราะบางเรคคอร์ดเก็บเป็น 'pro' แต่ชื่อแพ็กเกจเป็น 'PRO'

     สำคัญ: ลูกค้า 1 ร้านถือได้มากกว่า 1 แพ็กเกจ (เช่น PRO ของระบบแชท + ระบบบิล)
     ถ้าล็อกเหลือใบเดียวจะกลายเป็นต่อบิลไม่ได้เลย จึงอนุญาตแพ็กเกจตระกูลบิลไว้ด้วยเสมอ
     — ลูกค้าเลือกได้ว่าจะต่ออันไหน แต่ข้ามไปแพ็กเกจแชทระดับอื่นไม่ได้

     ถ้าจับคู่แพ็กเกจปัจจุบันไม่ได้ (ถูกปิด/เปลี่ยนชื่อ) จะปล่อยให้เลือกเองตามเดิม
     ดีกว่าปล่อยให้หน้าว่างจนต่ออายุไม่ได้เลย */
  const currentPlan = String((license && license.plan) || '').trim();
  const isTrial = currentPlan.toLowerCase() === 'trial';
  const ready = meLoaded && pkgLoaded;

  /* renew_hide_trial_v1: Trial ต้องแจกจากหลังบ้านเท่านั้น ห้ามให้ลูกค้าหยิบเอง
     ถ้าปล่อยไว้ในรายการ ลูกค้าที่อยู่ระหว่างทดลองใช้จะกด "ต่ออายุ" แล้วเลือก Trial
     ซ้ำได้เรื่อยๆ ราคา 0 บาท = ใช้ฟรีไม่มีวันหมด

     คัดด้วยชื่อไม่ใช่ราคา เผื่ออนาคตมีแพ็กเกจโปรโมชัน 0 บาทที่ตั้งใจให้เลือกได้จริง */
  const selectablePackages = packages.filter(
    p => String(p.name || '').toLowerCase() !== 'trial'
  );

  const currentPkg = currentPlan
    ? selectablePackages.find(p => String(p.name || '').toLowerCase() === currentPlan.toLowerCase())
    : null;

  /* ใส่แพ็กเกจตระกูลบิลไว้ให้เฉพาะร้านที่เปิดใช้ระบบบิล
     ใช้ !== false ตามแบบเดียวกับ Layout เพราะถ้าเผลอตัดออก ร้านที่ใช้บิลอยู่จริง
     จะต่ออายุบิลไม่ได้เลย ซึ่งเสียหายกว่าการเผลอโชว์ให้ร้านที่ไม่ได้ใช้ */
  const hasBill = !me || me.has_bill_feature !== false;
  const restricted = !isTrial && !!currentPkg;
  const allowedPackages = restricted
    ? selectablePackages.filter(p => p.id === currentPkg.id || (hasBill && isBillPackage(p)))
    : selectablePackages;

  /* เหลือทางเลือกเดียว = ล็อกไปเลย ไม่ต้องให้กดเลือก */
  const lockedPkg = (restricted && allowedPackages.length === 1) ? allowedPackages[0] : null;

  useEffect(() => {
    if (lockedPkg) setSelected(prev => (prev && prev.id === lockedPkg.id) ? prev : lockedPkg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedPkg && lockedPkg.id]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success((label || 'คัดลอก') + 'แล้ว'));
  };

  /* bank_accounts_crypto_v1: แยกช่องทางเป็นสองกล่อง
     แถวเก่าที่ยังไม่มี account_type ให้ถือเป็นธนาคาร (ตรงกับค่า default ในฐานข้อมูล) */
  const bankOnly = bankAccounts.filter(b => b.account_type !== 'crypto');
  const cryptoOnly = bankAccounts.filter(b => b.account_type === 'crypto');

  const orderRef = me ? (me.name + ' (' + me.email + ')') : '';
  const customerInfo = selected && me
    ? 'ร้าน: ' + me.name + '\nอีเมล: ' + me.email + '\nแพ็กเกจ: ' + selected.name + '\nราคา: ' + Number(selected.price).toLocaleString() + ' บาท\nระยะเวลา: ' + selected.duration_days + ' วัน'
    : '';

  return (
    <div style={{ minHeight:'100vh', background:'#0f172a', padding:'40px 20px' }}>
      <div style={{ maxWidth: step === 1 ? 1000 : 560, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
          <Link to="/" style={{ color:'#64748b', textDecoration:'none', display:'flex', alignItems:'center', gap:6, fontSize:14 }}>
            <ArrowLeft size={16} /> กลับ
          </Link>
        </div>

        <div style={{ textAlign:'center', marginBottom:30 }}>
          <h1 style={{ color:'#38bdf8', fontSize:24, fontWeight:700, margin:0 }}>💎 ต่ออายุ / Upgrade แพ็กเกจ</h1>
          {me && (
            <p style={{ color:'#94a3b8', marginTop:8, fontSize:14 }}>
              ร้าน: <strong style={{ color:'#f1f5f9' }}>{me.name}</strong>
            </p>
          )}
        </div>

        {/* Step indicator */}
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:36 }}>
          {['เลือกแพ็กเกจ','ชำระเงิน','ส่งสลิป'].map((s,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700,
                background: step > i+1 ? '#22c55e' : step === i+1 ? '#0ea5e9' : '#1e293b',
                color: step >= i+1 ? '#fff' : '#475569',
                border: '2px solid ' + (step >= i+1 ? 'transparent' : '#334155') }}>
                {step > i+1 ? <Check size={14} /> : (i+1)}
              </div>
              <span style={{ fontSize:13, color: step >= i+1 ? '#f1f5f9' : '#475569' }}>{s}</span>
              {i < 2 && <div style={{ width:30, height:2, background: step > i+1 ? '#22c55e' : '#1e293b' }} />}
            </div>
          ))}
        </div>

        {/* ============ STEP 1: เลือกแพ็กเกจ ============ */}
        {step === 1 && !ready && (
          <div style={{ textAlign:'center', color:'#64748b', fontSize:14, padding:'40px 0' }}>กำลังโหลด...</div>
        )}

        {step === 1 && ready && (
          <div>
            {/* renew_lock_plan_v1: มีแพ็กเกจอยู่แล้ว = ต่อของเดิมอย่างเดียว ไม่ต้องให้เลือก */}
            {lockedPkg ? (
              <>
                <h2 style={{ color:'#f1f5f9', textAlign:'center', marginBottom:8, fontSize:18 }}>ต่ออายุแพ็กเกจปัจจุบัน</h2>
                <p style={{ color:'#94a3b8', textAlign:'center', margin:'0 0 24px', fontSize:13 }}>
                  ระบบจะต่ออายุแพ็กเกจเดิมที่ร้านคุณใช้อยู่
                </p>
                <div style={{ maxWidth:300, margin:'0 auto' }}>
                  <PackageCard pkg={lockedPkg} locked />
                </div>
                <p style={{ color:'#64748b', textAlign:'center', margin:'20px 0 0', fontSize:12 }}>
                  ต้องการเปลี่ยนแพ็กเกจ?{' '}
                  <a href={`https://t.me/${ADMIN_TG}`} target="_blank" rel="noopener noreferrer" style={{ color:'#0ea5e9' }}>
                    ติดต่อแอดมิน @{ADMIN_TG}
                  </a>
                </p>
              </>
            ) : (
              <>
                <h2 style={{ color:'#f1f5f9', textAlign:'center', marginBottom:8, fontSize:18 }}>
                  {restricted ? 'เลือกรายการที่ต้องการต่ออายุ' : 'เลือกแพ็กเกจที่ต้องการ'}
                </h2>
                {/* renew_lock_plan_v2: ร้านที่ถือหลายแพ็กเกจ (เช่น แชท + บิล) เลือกได้ว่าจะต่ออันไหน */}
                {restricted && (
                  <p style={{ color:'#94a3b8', textAlign:'center', margin:'0 0 24px', fontSize:13 }}>
                    แสดงเฉพาะแพ็กเกจที่ร้านคุณใช้อยู่ — ต้องการเปลี่ยนแพ็กเกจ{' '}
                    <a href={`https://t.me/${ADMIN_TG}`} target="_blank" rel="noopener noreferrer" style={{ color:'#0ea5e9' }}>
                      ติดต่อแอดมิน
                    </a>
                  </p>
                )}
                {isTrial && (
                  <p style={{ color:'#94a3b8', textAlign:'center', margin:'0 0 24px', fontSize:13 }}>
                    ตอนนี้ร้านคุณอยู่ระหว่างทดลองใช้ — เลือกแพ็กเกจที่ต้องการใช้ต่อได้เลย
                  </p>
                )}
                {allowedPackages.length === 0 ? (
                  /* renew_hide_trial_v1: กันหน้าว่างถ้ามีแต่ Trial ในระบบ */
                  <div style={{ background:'#1e293b', borderRadius:12, padding:'28px 20px', textAlign:'center' }}>
                    <div style={{ fontSize:32, marginBottom:8 }}>📦</div>
                    <div style={{ color:'#f1f5f9', fontSize:14, fontWeight:600, marginBottom:6 }}>ยังไม่มีแพ็กเกจให้เลือก</div>
                    <div style={{ color:'#94a3b8', fontSize:12, marginBottom:14 }}>กรุณาติดต่อแอดมินเพื่อเลือกแพ็กเกจ</div>
                    <a href={`https://t.me/${ADMIN_TG}`} target="_blank" rel="noopener noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#0088cc', color:'#fff', padding:'9px 16px', borderRadius:8, textDecoration:'none', fontSize:13, fontWeight:600 }}>
                      <MessageCircle size={14} /> ติดต่อ @{ADMIN_TG}
                    </a>
                  </div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:18 }}>
                    {allowedPackages.map(pkg => (
                      <div key={pkg.id} onClick={() => setSelected(pkg)}>
                        <PackageCard pkg={pkg} isSelected={selected?.id === pkg.id} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div style={{ display:'flex', justifyContent:'center', marginTop:32 }}>
              <button disabled={!selected} onClick={() => setStep(2)}
                style={{
                  padding:'13px 40px', borderRadius:10,
                  background: selected ? '#0ea5e9' : '#475569',
                  color:'#fff', border:'none', fontSize:15, fontWeight:700,
                  cursor: selected ? 'pointer' : 'not-allowed',
                }}>
                ดำเนินการต่อ →
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 2: ชำระเงิน ============ */}
        {step === 2 && selected && (
          <div style={{ background:'#1e293b', borderRadius:16, padding:32 }}>
            <h2 style={{ margin:'0 0 20px', color:'#f1f5f9', fontSize:18, textAlign:'center' }}>💳 ข้อมูลการชำระเงิน</h2>

            {/* Selected package summary */}
            <div style={{ background:'#0f172a', borderRadius:12, padding:18, marginBottom:20, border:'1px solid #334155' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ color:'#94a3b8', fontSize:13 }}>แพ็กเกจ:</span>
                <span style={{ color:'#f1f5f9', fontWeight:700 }}>{selected.name.toUpperCase()}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ color:'#94a3b8', fontSize:13 }}>ระยะเวลา:</span>
                <span style={{ color:'#f1f5f9' }}>{selected.duration_days} วัน</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:10, borderTop:'1px solid #334155' }}>
                <span style={{ color:'#94a3b8', fontSize:14, fontWeight:600 }}>ยอดชำระ:</span>
                <span style={{ color:'#22c55e', fontSize:24, fontWeight:700 }}>{Number(selected.price).toLocaleString()} ฿</span>
              </div>
            </div>

            {/* Bank accounts
                bank_accounts_crypto_v1: ซ่อนกล่องนี้ถ้ามีแต่ช่องทางคริปโต จะได้ไม่โชว์หัวข้อลอยๆ ว่างเปล่า
                แต่ถ้าไม่มีช่องทางไหนเลย ยังต้องโชว์เพื่อขึ้นคำเตือนให้ติดต่อแอดมิน */}
            <div style={{ marginBottom:20, display: (!banksLoading && bankOnly.length === 0 && cryptoOnly.length > 0) ? 'none' : 'block' }}>
              <h3 style={{ color:'#f1f5f9', fontSize:14, margin:'0 0 12px', display:'flex', alignItems:'center', gap:6 }}>
                <CreditCard size={16} /> โอนผ่านธนาคาร
              </h3>
              {banksLoading ? (
                <div style={{ background:'#0f172a', borderRadius:10, padding:20, textAlign:'center', color:'#64748b', fontSize:13 }}>
                  กำลังโหลดข้อมูลบัญชี...
                </div>
              ) : bankAccounts.length === 0 ? (
                <div style={{ background:'#0f172a', borderRadius:10, padding:'20px 16px', border:'1px dashed #f59e0b', textAlign:'center' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>⚠️</div>
                  <div style={{ color:'#f1f5f9', fontSize:14, fontWeight:600, marginBottom:6 }}>ยังไม่มีบัญชีที่เปิดรับชำระ</div>
                  <div style={{ color:'#94a3b8', fontSize:12, marginBottom:14 }}>กรุณาติดต่อแอดมินเพื่อขอข้อมูลบัญชี</div>
                  <a href={`https://t.me/${ADMIN_TG}`} target="_blank" rel="noopener noreferrer"
                    style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#0088cc', color:'#fff', padding:'9px 16px', borderRadius:8, textDecoration:'none', fontSize:13, fontWeight:600 }}>
                    <MessageCircle size={14} /> ติดต่อ @{ADMIN_TG}
                  </a>
                </div>
              ) : bankOnly.map((b) => (
                <div key={b.id} style={{ background:'#0f172a', borderRadius:10, padding:'12px 14px', marginBottom:8, border:'1px solid #334155' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ color:'#f1f5f9', fontSize:13, fontWeight:600 }}>{b.bank_name}</div>
                      <div style={{ color:'#cbd5e1', fontSize:14, marginTop:2, fontFamily:'monospace' }}>{b.account_number}</div>
                      <div style={{ color:'#94a3b8', fontSize:12 }}>{b.account_name}</div>
                      {b.promptpay_id && (
                        <div style={{ color:'#0ea5e9', fontSize:11, marginTop:6, display:'flex', alignItems:'center', gap:4 }}>
                          <span style={{ background:'#0ea5e922', padding:'2px 6px', borderRadius:4 }}>PromptPay</span>
                          <span style={{ fontFamily:'monospace' }}>{b.promptpay_id}</span>
                        </div>
                      )}
                      {b.note && (
                        <div style={{ color:'#64748b', fontSize:11, marginTop:4, fontStyle:'italic' }}>📝 {b.note}</div>
                      )}
                    </div>
                    <button onClick={() => copyToClipboard(b.account_number, 'เลขบัญชี')}
                      style={{ background:'#0ea5e920', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', color:'#0ea5e9', flexShrink:0 }}>
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* bank_accounts_crypto_v1: แยกกล่องคริปโตออกจากธนาคาร
                ปนกันแล้วลูกค้าอ่านไม่ออกว่าอันไหนต้องเลือกเครือข่ายให้ตรง */}
            {cryptoOnly.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <h3 style={{ color:'#f1f5f9', fontSize:14, margin:'0 0 12px', display:'flex', alignItems:'center', gap:6 }}>
                  🪙 โอนด้วยเหรียญ (Crypto)
                </h3>
                {cryptoOnly.map((b) => (
                  <div key={b.id} style={{ background:'#0f172a', borderRadius:10, padding:'12px 14px', marginBottom:8, border:'1px solid #334155' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                          <span style={{ color:'#f1f5f9', fontSize:13, fontWeight:600 }}>{b.crypto_asset || b.bank_name}</span>
                          <span style={{ background:'#f59e0b22', color:'#f59e0b', padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700 }}>
                            {b.crypto_network}
                          </span>
                        </div>
                        <div style={{ color:'#cbd5e1', fontSize:12, marginTop:4, fontFamily:'monospace', wordBreak:'break-all' }}>{b.account_number}</div>
                        {b.account_name && <div style={{ color:'#94a3b8', fontSize:12, marginTop:2 }}>{b.account_name}</div>}
                        {b.note && (
                          <div style={{ color:'#64748b', fontSize:11, marginTop:4, fontStyle:'italic' }}>📝 {b.note}</div>
                        )}
                      </div>
                      <button onClick={() => copyToClipboard(b.account_number, 'ที่อยู่กระเป๋า')}
                        style={{ background:'#0ea5e920', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', color:'#0ea5e9', flexShrink:0 }}>
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {/* เตือนครั้งเดียวใต้กล่อง ไม่ซ้ำทุกใบ จะได้ไม่กลายเป็นข้อความที่คนเลิกอ่าน */}
                <div style={{ background:'#f59e0b11', border:'1px solid #f59e0b44', borderRadius:8, padding:'10px 12px', color:'#fbbf24', fontSize:11, lineHeight:1.6 }}>
                  ⚠️ ต้องโอนด้วยเครือข่ายที่ระบุไว้เท่านั้น โอนผิดเครือข่ายเงินจะหายถาวร กู้คืนไม่ได้ —
                  แนะนำให้คัดลอกที่อยู่กระเป๋าด้วยปุ่ม อย่าพิมพ์เอง
                </div>
              </div>
            )}

            {/* QR Promptpay placeholder */}
            <div style={{ marginBottom:20 }}>
              <h3 style={{ color:'#f1f5f9', fontSize:14, margin:'0 0 12px' }}>📱 QR Promptpay</h3>
              <div style={{ background:'#0f172a', borderRadius:10, padding:24, textAlign:'center', border:'1px dashed #475569', color:'#64748b', fontSize:13 }}>
                <div style={{ fontSize:48, marginBottom:8 }}>📱</div>
                (ไว้ใส่ QR Promptpay ทีหลัง)
              </div>
            </div>

            {/* Reference */}
            <div style={{ background:'#fef3c7', color:'#92400e', borderRadius:10, padding:'12px 14px', marginBottom:20, fontSize:13 }}>
              <strong>💡 อ้างอิงในใบโอน:</strong> {orderRef}
            </div>

            {/* Actions */}
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setStep(1)}
                style={{ flex:1, padding:'12px', borderRadius:10, background:'transparent', color:'#94a3b8', border:'1px solid #475569', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                ← ย้อนกลับ
              </button>
              <button onClick={() => setStep(3)}
                style={{ flex:2, padding:'12px', borderRadius:10, background:'#22c55e', color:'#fff', border:'none', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                ✓ ฉันชำระเงินแล้ว
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 3: อัปโหลดสลิป + ส่ง ============ */}
        {/* slip_upload_v1 */}
        {step === 3 && selected && me && !submitted && (
          <div style={{ background:"#1e293b", borderRadius:16, padding:32 }}>
            <h2 style={{ margin:"0 0 8px", color:"#f1f5f9", fontSize:20, textAlign:"center" }}>📤 อัปโหลดสลิปการโอน</h2>
            <p style={{ color:"#94a3b8", marginBottom:24, fontSize:13, textAlign:"center", lineHeight:1.6 }}>
              แนบสลิปการโอนเพื่อให้ผู้ให้บริการตรวจสอบและอนุมัติ
            </p>

            {/* Summary */}
            <div style={{ background:"#0f172a", borderRadius:10, padding:14, marginBottom:20, border:"1px solid #334155" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ color:"#94a3b8", fontSize:13 }}>แพ็กเกจ:</span>
                <strong style={{ color:"#f1f5f9", fontSize:13 }}>{selected.name.toUpperCase()}</strong>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ color:"#94a3b8", fontSize:13 }}>ยอด:</span>
                <strong style={{ color:"#22c55e", fontSize:14 }}>{Number(selected.price).toLocaleString()} ฿</strong>
              </div>
            </div>

            {/* Upload area */}
            <label htmlFor="slip-upload" style={{
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              padding:30, borderRadius:12, border:`2px dashed ${slipFile ? "#22c55e" : "#475569"}`,
              background: slipFile ? "#0c2a1a" : "#0f172a",
              cursor:"pointer", marginBottom:20, transition:"all 0.2s"
            }}>
              {slipFile ? (
                <>
                  <FileText size={36} color="#22c55e" />
                  <div style={{ color:"#22c55e", fontSize:14, fontWeight:600, marginTop:10 }}>{slipFile.name}</div>
                  <div style={{ color:"#94a3b8", fontSize:11, marginTop:4 }}>
                    {(slipFile.size / 1024).toFixed(1)} KB — คลิกเพื่อเปลี่ยนไฟล์
                  </div>
                </>
              ) : (
                <>
                  <Upload size={36} color="#64748b" />
                  <div style={{ color:"#cbd5e1", fontSize:14, marginTop:10, fontWeight:600 }}>คลิกเพื่อเลือกสลิป</div>
                  <div style={{ color:"#64748b", fontSize:11, marginTop:4 }}>JPG, PNG, PDF • สูงสุด 10MB</div>
                </>
              )}
              <input id="slip-upload" type="file" accept="image/*,.pdf" hidden
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f && f.size > 10 * 1024 * 1024) { toast.error("ไฟล์ใหญ่เกิน 10MB"); return; }
                  setSlipFile(f);
                }} />
            </label>

            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setStep(2)} disabled={submitting}
                style={{ flex:1, padding:"12px", borderRadius:10, background:"transparent", color:"#94a3b8", border:"1px solid #475569", fontSize:14, fontWeight:600, cursor: submitting?"not-allowed":"pointer" }}>
                ← ย้อนกลับ
              </button>
              <button onClick={handleSubmitSlip} disabled={!slipFile || submitting}
                style={{ flex:2, padding:"12px", borderRadius:10,
                  background:(!slipFile||submitting)?"#475569":"linear-gradient(135deg, #22c55e, #16a34a)",
                  color:"#fff", border:"none", fontSize:14, fontWeight:700,
                  cursor:(!slipFile||submitting)?"not-allowed":"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                {submitting ? <><Loader size={16} className="spin" /> กำลังส่ง...</> : <>📤 ส่งคำขอ</>}
              </button>
            </div>
          </div>
        )}

        {/* slip_upload_v1: หน้าสำเร็จ หลัง submit */}
        {step === 3 && submitted && (
          <div style={{ background:"#1e293b", borderRadius:16, padding:36, textAlign:"center" }}>
            <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
            <h2 style={{ margin:"0 0 12px", color:"#22c55e", fontSize:22 }}>ส่งคำขอสำเร็จ!</h2>
            <p style={{ color:"#cbd5e1", marginBottom:20, fontSize:14, lineHeight:1.7 }}>
              ผู้ให้บริการจะตรวจสอบและอนุมัติ<br/>ภายใน <strong style={{ color:"#fbbf24" }}>24 ชั่วโมง</strong>
            </p>
            {requestId && (
              <div style={{ background:"#0f172a", borderRadius:8, padding:"10px 14px", fontSize:11, color:"#64748b", marginBottom:20, fontFamily:"monospace" }}>
                Request ID: {requestId.substring(0, 8)}...
              </div>
            )}
            <p style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
              หากต้องการสอบถามเพิ่มเติม สามารถติดต่อผู้ให้บริการได้ที่:
            </p>
            <a href={"https://t.me/" + ADMIN_TG} target="_blank" rel="noreferrer"
              style={{
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                padding:"13px 20px", borderRadius:10,
                background:"linear-gradient(135deg, #0ea5e9, #2563eb)",
                color:"#fff", textDecoration:"none", fontSize:14, fontWeight:700,
                marginBottom:12
              }}>
              <MessageCircle size={18} /> ติดต่อผู้ให้บริการ (@{ADMIN_TG})
            </a>
            <Link to="/" style={{ color:"#64748b", fontSize:13, textDecoration:"none", display:"inline-block" }}>
              กลับหน้าหลัก
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
